import React, { useState, useMemo, useCallback, useEffect, memo } from "react";
import { FaSearch, FaTimes, FaUser } from "react-icons/fa";
import { formatPersonDisplayName } from "../../../utils/personnelDisplay";

const PersonnelSearch = ({
  personnel,
  onEmployeeSelect,
  selectedEmployee,
  onClearSelection,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Filter personnel based on search term
  const filteredPersonnel = useMemo(() => {
    if (!personnel?.length || !debouncedSearchTerm.trim()) return [];

    const term = debouncedSearchTerm.trim().toLowerCase();

    // Debug for specific search
    if (term === "6068128") {
      console.log("Searching for 6068128");
      console.log("Personnel data:", personnel);
      console.log("Personnel length:", personnel.length);
    }

    return personnel.filter((person) => {
      const firstName = person.firstName?.toLowerCase() || "";
      const lastName = person.lastName?.toLowerCase() || "";
      const nickname = (person.nickname || "").toLowerCase().trim();
      const personalNumber = person.personalNumber?.toString() || "";
      const fullName = `${firstName} ${lastName}`.trim();
      const fullNameReverse = `${lastName} ${firstName}`.trim();

      // Debug for specific search
      if (term === "6068128") {
        console.log("Checking person:", {
          firstName,
          lastName,
          personalNumber,
          fullName,
          fullNameReverse,
        });
      }

      // Check individual fields
      if (
        firstName.includes(term) ||
        lastName.includes(term) ||
        nickname.includes(term) ||
        personalNumber.includes(term)
      ) {
        if (term === "6068128") {
          console.log("Found match in individual fields");
        }
        return true;
      }

      // Check full name combinations
      if (fullName.includes(term) || fullNameReverse.includes(term)) {
        return true;
      }

      // Check multi-word searches
      const searchWords = term.split(/\s+/).filter((word) => word.length > 0);
      if (searchWords.length > 1) {
        return searchWords.every(
          (word) =>
            firstName.includes(word) ||
            lastName.includes(word) ||
            nickname.includes(word) ||
            personalNumber.includes(word),
        );
      }

      return false;
    });
  }, [personnel, debouncedSearchTerm]);

  // Debug final results
  if (debouncedSearchTerm === "6068128") {
    console.log("Filtered results for 6068128:", filteredPersonnel);
  }

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
    setShowSearchResults(true);
  }, []);

  const handleEmployeeSelect = useCallback(
    (employee) => {
      onEmployeeSelect(employee);
      setSearchTerm("");
      setShowSearchResults(false);
    },
    [onEmployeeSelect],
  );

  const handleClearSelection = useCallback(() => {
    onClearSelection();
    setSearchTerm("");
    setShowSearchResults(false);
  }, [onClearSelection]);

  const handleSearchFocus = useCallback(() => {
    setShowSearchResults(true);
  }, []);

  const handleSearchBlur = useCallback(() => {
    // Delay hiding to allow clicks on results
    setTimeout(() => setShowSearchResults(false), 200);
  }, []);

  return (
    <div className="search-section">
      <div className="search-container">
        <div className="search-input-wrapper">
          {!selectedEmployee ? (
            <FaSearch className="search-icon" />
          ) : (
            <FaTimes
              className="clear-search-icon"
              onClick={handleClearSelection}
            />
          )}
          <input
            type="text"
            className="search-input"
            placeholder="חפש חייל לפי שם פרטי, שם משפחה, כינוי, שם מלא או מ.א"
            value={
              selectedEmployee
                ? formatPersonDisplayName(selectedEmployee)
                : searchTerm
            }
            onChange={handleSearchChange}
            onFocus={handleSearchFocus}
            onBlur={handleSearchBlur}
            disabled={!!selectedEmployee}
          />
        </div>

        {showSearchResults && !selectedEmployee && (
          <div className="search-results">
            {filteredPersonnel.length > 0 ? (
              <>
                {filteredPersonnel.slice(0, 10).map((person) => (
                  <div
                    key={person.id}
                    className="search-result-item"
                    onClick={() => handleEmployeeSelect(person)}
                  >
                    <FaUser className="user-icon" />
                    <div className="employee-info">
                      <div className="employee-name">
                        {formatPersonDisplayName(person)}
                      </div>
                      <div className="employee-id">
                        מ.א: {person.personalNumber}
                      </div>
                    </div>
                  </div>
                ))}
                {filteredPersonnel.length > 10 && (
                  <div className="search-result-more">
                    ועוד {filteredPersonnel.length - 10} תוצאות...
                  </div>
                )}
              </>
            ) : (
              <div className="no-results">לא נמצאו חיילים</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(PersonnelSearch);
